// Carousel navigation controller
class CarouselNavigation extends HTMLElement {
  constructor() {
    super();
    this._initialized = false;
    this._rafId = null;
    this._handleArrowClick = this._handleArrowClick.bind(this);
    this._handleScroll = this._handleScroll.bind(this);
    this._handleResize = this._handleResize.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    const init = () => this._init();

    if ('IntersectionObserver' in window) {
      this._observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          this._observer.disconnect();
          this._observer = null;
          if ('requestIdleCallback' in window) {
            requestIdleCallback(init, { timeout: 800 });
          } else {
            requestAnimationFrame(init);
          }
        },
        { rootMargin: '200px' }
      );
      this._observer.observe(this);
      return;
    }

    if ('requestIdleCallback' in window) {
      requestIdleCallback(init, { timeout: 800 });
    } else {
      requestAnimationFrame(init);
    }
  }

  disconnectedCallback() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    if (this._scroller) {
      this._scroller.removeEventListener('scroll', this._handleScroll);
    }
    if (this._edgeObserver) {
      this._edgeObserver.disconnect();
      this._edgeObserver = null;
    }
    if (this._arrows) {
      this._arrows.forEach((arrow) => arrow.removeEventListener('click', this._handleArrowClick));
    }
    window.removeEventListener('resize', this._handleResize);
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }

  _findClosestSection(element) {
    let current = element;
    while (current && current !== document.body) {
      if (current.id && current.id.startsWith('shopify-section-')) {
        return current;
      }
      current = current.parentElement;
    }
    return null;
  }

  _init() {
    this._arrows = Array.from(this.querySelectorAll('[data-carousel-action]'));
    if (!this._arrows.length) return;

    this._prevBtn = this.querySelector('[data-carousel-action="prev"]');
    this._nextBtn = this.querySelector('[data-carousel-action="next"]');
    this._ref = this._arrows[0]?.dataset.carouselRef;
    if (!this._ref) return;

    const section = this._findClosestSection(this);
    this._searchScope = section || document;
    this._carousel = this._searchScope.querySelector(`slideshow-component[ref="${this._ref}"]`);
    if (!this._carousel) return;

    this._scroller = this._carousel.querySelector('slideshow-slides');
    if (!this._scroller) return;

    this._arrows.forEach((arrow) => {
      arrow.addEventListener('click', this._handleArrowClick);
    });

    window.addEventListener('resize', this._handleResize, { passive: true });

    // Reading scrollLeft/scrollWidth here, as each carousel nears the screen mid-scroll, made
    // the browser finish all pending style and layout for the page on the spot. Whether the
    // first and last slides are fully in view says the same thing without measuring.
    const slides = this._scroller.querySelectorAll('slideshow-slide');
    if ('IntersectionObserver' in window && slides.length) {
      this._first = slides[0];
      this._last = slides[slides.length - 1];
      this._edgeObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const inView = entry.isIntersecting && entry.intersectionRatio >= 0.95;
            if (entry.target === this._first && this._prevBtn) this._prevBtn.disabled = inView;
            if (entry.target === this._last && this._nextBtn) this._nextBtn.disabled = inView;
          }
        },
        { root: this._scroller, threshold: [0, 0.95, 1] }
      );
      this._edgeObserver.observe(this._first);
      if (this._last !== this._first) this._edgeObserver.observe(this._last);
      return;
    }

    this._scroller.addEventListener('scroll', this._handleScroll, { passive: true });
    this._updateArrowStates();
  }

  _ensureMetrics() {
    if (!this._scroller) return;
    const currentWidth = this._scroller.clientWidth;
    if (this._metricsWidth === currentWidth && this._slideWidth) return;

    const firstSlide = this._scroller.querySelector('slideshow-slide');
    if (!firstSlide) return;

    const slideRect = firstSlide.getBoundingClientRect();
    const computedStyle = window.getComputedStyle(this._scroller);
    const gap = parseFloat(computedStyle.gap) || 0;

    this._slideWidth = slideRect.width;
    this._gap = gap;
    this._metricsWidth = currentWidth;
  }

  _getScrollAmount() {
    this._ensureMetrics();
    if (!this._slideWidth) return 0;
    // Scroll by 1 slide so we don't skip products; previously was 2.5 which skipped too many (especially on mobile)
    const slidesPerClick = 1;
    return (this._slideWidth + (this._gap || 0)) * slidesPerClick;
  }

  _handleArrowClick(event) {
    event.preventDefault();
    if (!this._scroller) return;

    const action = event.currentTarget?.dataset?.carouselAction;
    const scrollAmount = this._getScrollAmount();
    if (!scrollAmount) return;

    const currentScroll = this._scroller.scrollLeft;
    if (action === 'prev') {
      const newScroll = Math.max(0, currentScroll - scrollAmount);
      this._scroller.scrollTo({ left: newScroll, behavior: 'smooth' });
    } else if (action === 'next') {
      const maxScroll = this._scroller.scrollWidth - this._scroller.clientWidth;
      const newScroll = Math.min(maxScroll, currentScroll + scrollAmount);
      this._scroller.scrollTo({ left: newScroll, behavior: 'smooth' });
    }
  }

  _handleScroll() {
    if (this._rafId) return;
    this._rafId = requestAnimationFrame(() => {
      this._rafId = null;
      this._updateArrowStates();
    });
  }

  _handleResize() {
    this._metricsWidth = null;
    this._slideWidth = null;
    this._gap = null;
    if (!this._edgeObserver) this._updateArrowStates();
  }

  _updateArrowStates() {
    if (!this._scroller) return;
    if (this._prevBtn) {
      this._prevBtn.disabled = this._scroller.scrollLeft <= 0;
    }
    if (this._nextBtn) {
      const isAtEnd = this._scroller.scrollLeft >= (this._scroller.scrollWidth - this._scroller.clientWidth - 1);
      this._nextBtn.disabled = isAtEnd;
    }
  }
}

if (!customElements.get('carousel-navigation')) {
  customElements.define('carousel-navigation', CarouselNavigation);
}
